import type { TovProfileResponse } from "contracts/api/client-tov/responses";
import type { TovSourceResponse } from "contracts/api/client-tov/sources.responses";
import type { TovVersionResponse } from "contracts/api/client-tov/version.responses";
import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { Show } from "solid-js";
import { TOV_GUIDELINE_STATE } from "../../enums/tov";
import { relativeAge } from "../../ui/format";
import { Metric } from "../../ui/Metric";
import { guidanceProvenance } from "../tov/guidanceProvenance";
import { sourceCountLabel, statusLabel } from "../tov/guidelineCoverage";
import type { SourceTrust } from "../tov/guidelineModel";
export function ActiveReferenceLayer(props: {
  websiteId: string;
  profile: TovProfileResponse;
  version: TovVersionResponse | undefined;
  sources: TovSourceResponse[] | undefined;
  sourceCoverage: string;
  linkedEvidenceCount: number;
  unestablishedEvidenceCount: number;
  traceability: string;
  trust: SourceTrust;
  site: OverviewResponse["site"] | null;
}) {
  const profileMatches = () =>
    props.site ? props.profile.clientId === props.site.clientId : true;
  const provenance = () => guidanceProvenance(props.sources);
  return (
    <section class="signal-panel signal-reference-layer">
      <div class="signal-panel-head">
        <div>
          <span class="signal-mini-kicker">Guidance used in this scan</span>
          <h2>{props.profile.name}</h2>
          <p>
            Guidance profile, source coverage and evidence traceability for the
            Content Fitness assessments that used it.
          </p>
        </div>
        <a
          class="signal-primary-link"
          href={`/w/${props.websiteId}/scan/brand-guidelines/manage`}
        >
          Open full guideline workspace
        </a>
      </div>
      <div class="signal-reference-grid">
        <Metric label="Version" value={props.version?.label ?? "Processing"} />
        <Metric
          label="Status"
          value={statusLabel(props.version?.status ?? null)}
        />
        <Metric
          label="Activated"
          value={
            props.version?.activatedAt
              ? relativeAge(new Date(props.version.activatedAt))
              : "Not active"
          }
        />
        <Metric label="Sources" value={sourceCountLabel(props.sources)} />
        <Metric label="Sources processed" value={props.sourceCoverage} />
        <Metric
          label="Linked evidence"
          value={`${props.linkedEvidenceCount}`}
        />
        <Metric
          label="Evidence without established source"
          value={`${props.unestablishedEvidenceCount}`}
        />
        <Metric label="Traceability" value={props.traceability} />
        <Metric label="Guidance origin" value={provenance().label} />
        <Metric
          label="Ownership"
          value={profileMatches() ? props.trust.label : "Review required"}
        />
      </div>
      <div
        class={`signal-provenance-callout is-${provenance().state}`}
        role="note"
      >
        <strong>{provenance().label}</strong>
        <p>{provenance().description}</p>
      </div>
      <Show
        when={
          !profileMatches() || props.trust.state !== TOV_GUIDELINE_STATE.VALID
        }
      >
        <div class="signal-trust-callout">
          <strong>Source review required</strong>
          <p>
            {!profileMatches()
              ? "The selected profile does not match the current website client."
              : (props.trust.reason ?? "Source ownership is not established.")}
          </p>
        </div>
      </Show>
    </section>
  );
}
